import GridTable from '@/components/table/GridTable'
import { HStack, Icon } from '@musinsa/mwms-ds'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { COLUMN_WIDTH, GRID_SIZE } from '@/utils/grid/grid'
import Text from '@/components/content/Text'
import Button from '@/components/content/Button'
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { isEmpty } from 'lodash-es'
import { colorSystem } from '@/styles/theme'
import { GridTableProps } from '@/components/table/grid_types'
import { CATEGORY_GLOBAL_SETTLEMENT_FLAG, SettlementAmountSummaryReq, SettlementStatisticsRes } from '@/services/settlement/settlementManagement/types'
import { Link } from 'react-router-dom'
import { PATHS } from '@/utils/routes/paths'
import { Utils } from '@/utils/Utils'
import { AmountCell } from '@/pages/settlement/SettlementManagement/components'
import { GridTooltip } from '@/pages/settlement/SettlementManagement/components/GridTooltip'
import { SETTLEMENT_CLOSED_STATUS_MAP } from '@/pages/settlement/SettlementManagement/consts/consts'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { defaultColDef } from '@/components/table/grid_table_options'
import styled from 'styled-components'
import { HeaderTooltip } from './HeaderTooltip'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import dayjs from 'dayjs'
import { tokens } from '@musinsa/layer-tokens/desktop'
import { useGetCountries } from '@/services/commonCode/useCommonCodeQueries'
import { useFeatureFlag } from '@/services/feature/useFeatureQueries'
import { useMeasurementSettlementEnabled } from '@/pages/settlement/SettlementManagement/hooks/useMeasurementSettlementEnabled'

export type SettlementManagementTableRef = {
  clearSelection: () => void
}

type SettlementManagementTableProps = Pick<GridTableProps<string>, 'isLoading' | 'onExportData' | 'onChangePage'> & {
  rows: SettlementStatisticsRes[]
  pageInfo?: PageInfoType
  onSettlementConfirm: (ids: string[]) => void
  bulkRegisterAdjustmentOptions?: Array<{
    label: string
    onClick: () => void
    disabled?: boolean
  }>
}

const PartnerIdCell = ({ value, data, reload }: ICellRendererParams<SettlementStatisticsRes> & { reload: () => void }) => (
  <Link
    to={`${PATHS['bl.bl03']}/${value}?settlementMonth=${data?.settlementMonth}`}
    onClick={() => {
      MenuStorage.menuOnSession.setLinkSearch({
        menuCode: 'bl03',
        query: { settlementMonth: data?.settlementMonth },
        link: `${PATHS['bl.bl03']}/${value}`,
      })
      reload()
    }}
  >
    <Text
      kind="Body_12_regular"
      style={{
        textDecoration: 'underline',
      }}
      color="#245EFF"
    >
      {value}
    </Text>
  </Link>
)

const TotalAmountCell = ({ value, data }: ICellRendererParams<SettlementStatisticsRes>) => (
  <AmountCell originAmount={value} discountedAmount={data?.totalAmount.discountedAmount} />
)

const InventoryAmountCell = ({ value, data }: ICellRendererParams<SettlementStatisticsRes>) => (
  <AmountCell originAmount={value} discountedAmount={data?.inventoryAmount.discountedAmount} />
)

const InboundAmountCell = ({ value, data }: ICellRendererParams<SettlementStatisticsRes>) => (
  <AmountCell originAmount={value} discountedAmount={data?.inboundAmount.discountedAmount} />
)

const OutboundAmountCell = ({ value, data }: ICellRendererParams<SettlementStatisticsRes>) => (
  <AmountCell originAmount={value} discountedAmount={data?.customerOutboundAmount.discountedAmount} />
)

const StoreOutboundAmountCell = ({ value, data }: ICellRendererParams<SettlementStatisticsRes>) => (
  <AmountCell originAmount={value} discountedAmount={data?.storeOutboundAmount.discountedAmount} />
)

const ReturnInboundAmountCell = ({ value, data }: ICellRendererParams<SettlementStatisticsRes>) => (
  <AmountCell originAmount={value} discountedAmount={data?.returnInboundAmount.discountedAmount} />
)

// LOGISPRD-16794 실측비. 필드가 없는 응답(BE 미배포)은 0원
const MeasurementAmountCell = ({ data }: ICellRendererParams<SettlementStatisticsRes>) => (
  <AmountCell originAmount={data?.measurementAmount?.originAmount ?? 0} discountedAmount={data?.measurementAmount?.discountedAmount ?? 0} />
)

export const SettlementManagementTable = forwardRef<SettlementManagementTableRef, SettlementManagementTableProps>(
  ({ rows, pageInfo, isLoading, onChangePage, onExportData, onSettlementConfirm, bulkRegisterAdjustmentOptions }, ref) => {
    const { reload } = useMenuOnSession()

    const { parsedQs } = useParsedQuery<SettlementAmountSummaryReq>()

    // LP9182 국가 차원 노출 flag. OFF 면 국가 컬럼 미노출(레거시 동일)
    const { data: isCategoryGlobalSettlementEnabled } = useFeatureFlag(CATEGORY_GLOBAL_SETTLEMENT_FLAG)

    // LOGISPRD-16794 실측비 flag. ON 이면 반품비(E) 뒤에 실측비(F)를 넣고 기타조정비를 G 로 민다
    const isMeasurementEnabled = useMeasurementSettlementEnabled()

    // LP9182 — countries(group_concat 코드 "KR,JP") → code→name 매핑. 순서 미보장이라 useGetCountries 순서로 고정 정렬해 안정 표시(§4.3)
    // flag OFF 면 조회 자체 차단(no-op·실패 알럿 방지)
    const { data: countries } = useGetCountries(!!isCategoryGlobalSettlementEnabled)
    const codeToName = useMemo(() => new Map((countries ?? []).map((c) => [c.code, c.name])), [countries])
    const orderIndex = useMemo(() => new Map((countries ?? []).map((c, i) => [c.code, i])), [countries])
    const renderCountries = useCallback(
      (csv?: string | null) => {
        if (!csv) return '-'
        const names = csv
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .sort((a, b) => (orderIndex.get(a) ?? 99) - (orderIndex.get(b) ?? 99))
          .map((code) => codeToName.get(code) ?? code)
        return names.length ? names.join(', ') : '-'
      },
      [codeToName, orderIndex],
    )

    const $grid = useRef<AgGridReact>(null)
    const [selectedRows, setSelectedRows] = useState<SettlementStatisticsRes[]>($grid.current?.api?.getSelectedRows() || [])
    const hasSelection = !isEmpty(selectedRows)

    const handleClickSettlementConfirm = () => {
      const selectedRowIds = selectedRows.map((row) => row.partnerId)
      onSettlementConfirm(selectedRowIds)
    }

    useImperativeHandle(ref, () => ({
      clearSelection: () => {
        $grid.current?.api?.deselectAll()
      },
    }))

    const columnDefs: ColDef<SettlementStatisticsRes>[] = useMemo(
      () => [
        {
          field: 'settlementClosedStatus',
          headerName: '상태',
          width: COLUMN_WIDTH.yna,
          pinned: true,
          valueFormatter: ({ value }) => SETTLEMENT_CLOSED_STATUS_MAP[value as SettlementStatisticsRes['settlementClosedStatus']],
        },
        {
          field: 'partnerId',
          headerName: '업체 아이디',
          width: COLUMN_WIDTH.status,
          pinned: true,
          cellRenderer: PartnerIdCell,
          cellRendererParams: { reload },
        },
        // LP9182 flag OFF 면 국가 컬럼 미노출
        ...(isCategoryGlobalSettlementEnabled
          ? [
              {
                // LP9182 — 파트너별 국가집합. 국가 필터와 무관하게 항상 전체 집합 노출(금액만 필터)
                colId: 'countries',
                headerName: '국가',
                width: COLUMN_WIDTH.status,
                pinned: true,
                valueGetter: ({ data }) => renderCountries(data?.countries),
              } as ColDef<SettlementStatisticsRes>,
            ]
          : []),
        {
          field: 'totalAmount.originAmount',
          headerName: '정산금액',
          width: COLUMN_WIDTH.status,
          pinned: true,
          headerComponent: HeaderTooltip,
          headerComponentParams: { headerName: '정산금액', content: isMeasurementEnabled ? '정산금액 : A+B+C+D+E+F+G' : '정산금액 : A+B+C+D+E+F' },
          autoHeight: true,
          cellRenderer: TotalAmountCell,
        },
        {
          field: 'inventoryAmount.originAmount',
          headerName: '보관비(A)',
          width: COLUMN_WIDTH.status,
          autoHeight: true,
          headerClass: 'ag-right-aligned-header',
          cellRenderer: InventoryAmountCell,
        },
        {
          field: 'inboundAmount.originAmount',
          headerName: '입고비(B)',
          width: COLUMN_WIDTH.status,
          autoHeight: true,
          headerClass: 'ag-right-aligned-header',
          cellRenderer: InboundAmountCell,
        },
        {
          field: 'customerOutboundAmount.originAmount',
          headerName: '고객출고비(C)',
          width: COLUMN_WIDTH.status,
          autoHeight: true,
          headerClass: 'ag-right-aligned-header',
          cellRenderer: OutboundAmountCell,
        },
        {
          field: 'storeOutboundAmount.originAmount',
          headerName: '매장출고비(D)',
          width: COLUMN_WIDTH.status,
          autoHeight: true,
          headerClass: 'ag-right-aligned-header',
          cellRenderer: StoreOutboundAmountCell,
        },
        {
          field: 'returnInboundAmount.originAmount',
          headerName: '반품비(E)',
          width: COLUMN_WIDTH.status,
          autoHeight: true,
          headerClass: 'ag-right-aligned-header',
          cellRenderer: ReturnInboundAmountCell,
        },
        ...(isMeasurementEnabled
          ? [
              {
                field: 'measurementAmount.originAmount',
                headerName: '실측비(F)',
                width: COLUMN_WIDTH.status,
                autoHeight: true,
                headerClass: 'ag-right-aligned-header',
                cellRenderer: MeasurementAmountCell,
              } as ColDef<SettlementStatisticsRes>,
            ]
          : []),
        {
          field: 'etcAdjustmentAmount',
          headerName: isMeasurementEnabled ? '기타조정비(G)' : '기타조정비(F)',
          width: COLUMN_WIDTH.status,
          headerClass: 'ag-right-aligned-header',
          cellClass: 'ag-right-aligned-cell',
          valueFormatter: ({ value }) => (value ? `${Utils.getCommaNum(value)}원` : '-'),
        },
        {
          field: 'promotionDiscountedAmount',
          headerName: '프로모션 할인',
          width: COLUMN_WIDTH.status,
          headerClass: ['ag-right-aligned-header', 'no-divider'],
          cellClass: 'ag-right-aligned-cell',
          valueFormatter: ({ value }) => (value ? `${Utils.getCommaNum(value)}원` : '-'),
        },
      ],
      [reload, renderCountries, isCategoryGlobalSettlementEnabled, isMeasurementEnabled],
    )

    const onGridReady = () => {
      $grid.current?.api?.setHeaderHeight(40)
    }

    return (
      <TableWrap full gap={16}>
        <GridTable
          ref={$grid}
          gridKey="BL03_HEAD"
          onGridReady={onGridReady}
          isLoading={isLoading}
          autoSizePadding={2}
          title={
            <HStack gap={16}>
              <HStack gap={2} style={{ flex: '0 0 auto', color: hasSelection ? tokens.layerDesktop['fg-accent'] : colorSystem.text.common.tertiary }}>
                <Text kind="Body_14_bold" color="inherit">
                  {selectedRows.length}
                </Text>
                <Text kind="Body_14_regular" color="inherit">
                  건 선택
                </Text>
              </HStack>
              <Button fit size="sm" kind="secondary" type="button" disabled={!hasSelection} onClick={handleClickSettlementConfirm}>
                마감확정
              </Button>
            </HStack>
          }
          defaultColDef={{ ...defaultColDef, suppressMenu: true, tooltipComponent: GridTooltip, autoHeight: true }}
          columnDefs={columnDefs}
          rowData={rows}
          rowSelectable
          isRowSelectable={(node) => {
            return (
              parsedQs.settlementMonth !== undefined &&
              dayjs().isAfter(dayjs(node.data?.settlementEndDate), 'day') &&
              node.data?.settlementClosedStatus !== 'COMPLETED'
            )
          }}
          onSelectionChanged={(event) => setSelectedRows(event.api.getSelectedRows())}
          getRowId={({ data }) => data.partnerId}
          pagination
          countPerPage={GRID_SIZE.SETTLEMENT}
          totalCount={pageInfo?.totalCount ?? 0}
          currentPage={pageInfo?.page}
          onChangePage={onChangePage}
          onExportData={onExportData}
          exportType="private"
          exportWithPassword={false}
          exportButtonLabel={
            <Text kind="Body_14_medium">
              <Icon name="download" size={16} style={{ marginRight: '4px' }} /> 엑셀 다운로드
            </Text>
          }
          optionActionButton={
            bulkRegisterAdjustmentOptions && bulkRegisterAdjustmentOptions.length > 0
              ? {
                  content: <Text kind="Body_14_medium">기타조정비 일괄 등록</Text>,
                  options: bulkRegisterAdjustmentOptions,
                  disabled: rows.every((row) => row.settlementClosedStatus === 'COMPLETED'),
                  kind: 'secondary',
                  size: 'sm',
                }
              : undefined
          }
          tooltipShowDelay={0}
          enableBrowserTooltips={false}
        />
      </TableWrap>
    )
  },
)

const TableWrap = styled(HStack)`
  /* .ag-header-row {
    height: 40px !important;
  }
  .ag-header-cell {
    height: 40px !important;
  }
  .ag-header-cell-label {
    height: 40px !important;
  } */
  .ag-cell-wrapper {
    height: 52px;
  }
`
